import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { motion } from "framer-motion";
import {
    Globe2,
    Plus,
    Trash2,
    Webhook,
    Shield,
    Settings2,
    Timer,
    HelpCircle,
    AlertCircle,
    Zap,
    InfoIcon,
    Plug,
    Key,
    Clock
} from "lucide-react";
import { NumberInput } from "@/components/ui/number-input";
import { HeaderTypeSelect } from "../HeaderTypeSelect";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import { useTenant } from "@/context/TenantContext";
import { ServerConfig } from "@/api/tools/tools.types";
import { Switch } from "@/components/ui/switch";
import { SwitchWithLabel } from "@/components/ui/switch-with-label";

interface ConnectionState extends Omit<ServerConfig, 'url'> {
    url: string;
    isAsync: boolean;
}

interface ConnectionFormProps {
    value: ConnectionState;
    onChange: (data: ConnectionState) => void;
    isReadOnly?: boolean;
}

export function ConnectionForm({ value, onChange, isReadOnly = false }: ConnectionFormProps) {
    const { branding } = useTenant();
    const [formData, setFormData] = useState<ConnectionState>(value);
    const [headers, setHeaders] = useState<{ key: string; type: string; value: string }[]>(
        Object.entries(value.headers || {}).map(([key, value]) => ({
            key,
            type: "String",
            value,
        }))
    );

    // Memoize the update function
    const updateParent = useCallback((newData: ConnectionState) => {
        // Add /sync suffix only when Async is disabled (like phone numbers)
        const finalUrl = newData.url ? 
            (!newData.isAsync ? `${newData.url.replace(/\/sync\/?$/, '')}/sync` : newData.url.replace(/\/sync\/?$/, '')) 
            : '';
        
        const dataWithConditionalSync = {
            ...newData,
            url: finalUrl
        };
        const isEqual = JSON.stringify(dataWithConditionalSync) === JSON.stringify(value);
        if (!isEqual) {
            onChange(dataWithConditionalSync);
        }
    }, [value, onChange]);

    // Update local state when value prop changes
    useEffect(() => {
        const valueWithoutSync = {
            ...value,
            // Remove /sync from the pre-filled server URL (like phone numbers)
            url: value.url ? value.url.replace(/\/sync\/?$/, '') : ''
        };
        const isEqual = JSON.stringify(formData) === JSON.stringify(valueWithoutSync);
        if (!isEqual) {
            setFormData(valueWithoutSync);
            setHeaders(Object.entries(value.headers || {}).map(([key, value]) => ({
                key,
                type: "String",
                value,
            })));
        }
    }, [value]);

    const handleAsyncChange = (checked: boolean) => {
        const newData = { ...formData, isAsync: checked };
        setFormData(newData);
        updateParent(newData);
    };

    const handleUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        // Remove any existing /sync suffix before setting the value (like phone numbers)
        const value = e.target.value.replace(/\/sync\/?$/, '');
        const newData = { ...formData, url: value };
        setFormData(newData);
        updateParent(newData);
    };

    const handleSecretChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newData = { ...formData, secret: e.target.value };
        setFormData(newData);
        updateParent(newData);
    };

    const handleTimeoutChange = (value: number | undefined) => {
        const newData = { ...formData, timeoutSeconds: value };
        setFormData(newData);
        updateParent(newData);
    };

    const addHeader = () => {
        const newHeaders = [...headers, { key: "", type: "String", value: "" }];
        setHeaders(newHeaders);
        
        // Update form data with new headers
        const headerObj = newHeaders.reduce((acc, { key, value }) => {
            if (key) acc[key] = value;
            return acc;
        }, {} as Record<string, string>);
        
        const newData = { ...formData, headers: headerObj };
        setFormData(newData);
        updateParent(newData);
    };

    const removeHeader = (index: number) => {
        const newHeaders = headers.filter((_, i) => i !== index);
        setHeaders(newHeaders);
        
        // Update form data with new headers
        const headerObj = newHeaders.reduce((acc, { key, value }) => {
            if (key) acc[key] = value;
            return acc;
        }, {} as Record<string, string>);
        
        const newData = { ...formData, headers: headerObj };
        setFormData(newData);
        updateParent(newData);
    };

    const updateHeaderField = (index: number, field: keyof typeof headers[0], value: string) => {
        const newHeaders = [...headers];
        newHeaders[index] = { ...newHeaders[index], [field]: value };
        setHeaders(newHeaders);
        
        // Update form data with new headers
        const headerObj = newHeaders.reduce((acc, { key, value }) => {
            if (key) acc[key] = value;
            return acc;
        }, {} as Record<string, string>);
        
        const newData = { ...formData, headers: headerObj };
        setFormData(newData);
        updateParent(newData);
    };

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.15 }}
            className="space-y-6"
        >
            {/* Server Configuration Card */}
            <Card className="shadow-sm">
                <CardHeader className="border-b bg-muted/20">
                    <div className="flex items-center gap-3">
                        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
                            <Plug className="h-4 w-4 text-primary" />
                        </div>
                        <div className="flex-1 space-y-1">
                            <CardTitle>Server Configuration</CardTitle>
                            <CardDescription>
                                Configure your server endpoint and authentication details
                            </CardDescription>
                        </div>
                    </div>
                </CardHeader>
                <CardContent className="space-y-6">
                    <SwitchWithLabel
                        id="asyncTool"
                        label="Async Tool"
                        description="When enabled, the assistant won't wait for server response"
                        icon={Zap}
                        checked={formData.isAsync}
                        onCheckedChange={handleAsyncChange}
                        disabled={isReadOnly}
                        tooltip="This setting defines whether the assistant will move forward or wait for your server to respond. Enable this if you just want to trigger something on your server."
                    />

                    <div className="space-y-2">
                        <Label htmlFor="serverUrl" className="flex items-center gap-2 text-sm font-medium">
                            <Globe2 className="h-4 w-4 text-primary" />
                            Server URL
                            <TooltipProvider delayDuration={200}>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <InfoIcon className="h-4 w-4 text-muted-foreground cursor-help" />
                                    </TooltipTrigger>
                                    <TooltipContent className="max-w-[400px] p-3 space-y-2">
                                        <p className="text-xs text-muted-foreground">
                                            Currently, this system is integrated exclusively with Our Workflows in a closed environment.
                                        </p>
                                        <div className="space-y-1.5">
                                            <p className="text-xs font-medium">Valid URL format:</p>
                                            <code className="text-xs bg-accent px-1.5 py-1 rounded block">https://workflow.voicebot.studio/api/v1/webhooks/[ID]</code>
                                            <p className="text-xs text-muted-foreground">
                                                The <code className="bg-accent px-1 py-0.5 rounded">/sync</code> suffix is automatically appended when "Async Tool" is disabled.
                                            </p>
                                        </div>
                                    </TooltipContent>
                                </Tooltip>
                            </TooltipProvider>
                        </Label>
                        <div className="relative">
                            <Input
                                id="serverUrl"
                                placeholder="https://workflow.voicebot.studio/api/v1/webhooks/[ID]"
                                className={`h-9 ${!formData.isAsync ? 'pr-[60px]' : ''}`}
                                value={formData.url}
                                onChange={handleUrlChange}
                                autoComplete="off"
                                type="url"
                                spellCheck="false"
                                disabled={isReadOnly}
                            />
                            {!formData.isAsync && (
                                <div className="absolute right-0 top-0 bottom-0 flex items-center px-3 pointer-events-none border-l bg-muted/40">
                                    <span className="text-xs text-muted-foreground">/sync</span>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                            <Label htmlFor="secretToken" className="flex items-center gap-2 text-sm font-medium">
                                <Key className="h-4 w-4 text-primary" />
                                Secret Token
                            </Label>
                            <Input
                                id="secretToken"
                                type="password"
                                placeholder="Enter secret token"
                                className="h-9"
                                value={formData.secret}
                                onChange={handleSecretChange}
                                autoComplete="new-password"
                                disabled={isReadOnly}
                            />
                            <p className="text-xs text-muted-foreground">
                                Sent as <code className="bg-muted px-1 py-0.5 rounded text-xs">x-voicebot-secret</code> header
                            </p>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="timeout" className="flex items-center gap-2 text-sm font-medium">
                                <Clock className="h-4 w-4 text-primary" />
                                Timeout (seconds)
                            </Label>
                            <NumberInput
                                value={formData.timeoutSeconds}
                                onValueChange={handleTimeoutChange}
                                min={1}
                                max={120}
                                stepper={1}
                                placeholder="20"
                                disabled={isReadOnly}
                            />
                            <p className="text-xs text-muted-foreground">
                                Request timeout: 1-120 seconds
                            </p>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Headers Card */}
            <Card className="shadow-sm">
                <CardHeader className="border-b bg-muted/20">
                    <div className="flex items-center gap-3">
                        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
                            <Settings2 className="h-4 w-4 text-primary" />
                        </div>
                        <div className="flex-1 space-y-1">
                            <CardTitle>Custom Headers</CardTitle>
                            <CardDescription>
                                Add custom headers to include in requests
                            </CardDescription>
                        </div>
                        {headers.length > 0 && (
                            <Button
                                size="sm"
                                onClick={addHeader}
                                className="gap-2"
                                disabled={isReadOnly}
                            >
                                <Plus className="h-4 w-4" />
                                Add Header
                            </Button>
                        )}
                    </div>
                </CardHeader>
                <CardContent className="space-y-4">
                    {headers.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-12 text-center">
                            <div className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-muted mb-4">
                                <Settings2 className="h-8 w-8 text-muted-foreground" />
                            </div>
                            <h3 className="text-sm font-medium mb-1">No Custom Headers</h3>
                            <p className="text-sm text-muted-foreground mb-4 max-w-sm">
                                Add custom headers to include additional data in your requests
                            </p>
                            <Button
                                size="sm"
                                onClick={addHeader}
                                className="gap-2"
                                disabled={isReadOnly}
                            >
                                <Plus className="h-4 w-4" />
                                Add Your First Header
                            </Button>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {headers.map((header, index) => (
                                <div key={index} className="flex gap-2 items-start">
                                    <Input
                                        placeholder="Key"
                                        value={header.key}
                                        onChange={(e) => updateHeaderField(index, 'key', e.target.value)}
                                        className="h-9 w-1/4"
                                        disabled={isReadOnly}
                                    />
                                    <HeaderTypeSelect
                                        value={header.type}
                                        onChange={(value) => updateHeaderField(index, 'type', value)}
                                        disabled={isReadOnly}
                                    />
                                    <div className="relative flex-1">
                                        <Input
                                            placeholder="Value"
                                            value={header.value}
                                            onChange={(e) => updateHeaderField(index, 'value', e.target.value)}
                                            className="h-9 w-full pr-10"
                                            disabled={isReadOnly}
                                        />
                                        <TooltipProvider delayDuration={200}>
                                            <Tooltip>
                                                <TooltipTrigger asChild>
                                                    <button
                                                        className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                                                        onClick={(e) => e.preventDefault()}
                                                    >
                                                        <HelpCircle className="h-3.5 w-3.5" />
                                                    </button>
                                                </TooltipTrigger>
                                                <TooltipContent className="max-w-xs">
                                                    <p className="text-xs">Supports Liquid templating. <a
                                                        href="https://shopify.github.io/liquid/basics/introduction/"
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="text-primary hover:underline"
                                                    >
                                                        Learn more
                                                    </a></p>
                                                </TooltipContent>
                                            </Tooltip>
                                        </TooltipProvider>
                                    </div>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => removeHeader(index)}
                                        className="gap-1.5 text-destructive hover:text-destructive hover:bg-destructive/10 h-9 px-2"
                                        disabled={isReadOnly}
                                    >
                                        <Trash2 className="h-3.5 w-3.5" />
                                        Delete
                                    </Button>
                                </div>
                            ))}
                        </div>
                    )}
                </CardContent>
            </Card>
        </motion.div>
    );
}
